import { type ComponentProps, useCallback } from 'react';
import { getColorVar, MiniCartButton as BaseMiniCartButton, cn } from '@kidslabs/mario-nextjs-react-ui-core';
import { useCartTotalItems } from '../../hooks';
import { observer } from '@legendapp/state/react';
import { useRouter } from 'next/navigation';
import styles from './mini-cart-button.module.css';
import { MINI_CART_BUTTON_SELECTOR } from '../../utils/cart-fly-item/cart-fly-item.constants';

// Re-export for external usage
export { MINI_CART_BUTTON_SELECTOR };

type BaseMiniCartButtonProps = ComponentProps<typeof BaseMiniCartButton>;

export interface MiniCartButtonProps extends BaseMiniCartButtonProps {
    /**
     * Callback for desktop click - opens cart drawer/sidebar
     * If not provided, will navigate to cart page
     */
    onDesktopClick?: () => void;
    /**
     * Function to check if current device is mobile
     * @default returns false (desktop behavior)
     */
    isMobile?: boolean;
    /**
     * Cart page route path
     * @default '/payment/cart'
     */
    cartPagePath?: string;
}

export const MiniCartButton = observer((props: MiniCartButtonProps) => {
    const { onDesktopClick, isMobile = false, cartPagePath = '/payment/cart', ...restProps } = props;

    const router = useRouter();
    const cartTotalItems$ = useCartTotalItems();
    const amoutCartItem = cartTotalItems$.get();

    const goToCartPage = useCallback(() => {
        router.push(cartPagePath);
    }, [router, cartPagePath]);

    const handleClick = useCallback(() => {
        // On mobile: always navigate to cart page
        // On desktop: use function injection if provided, otherwise navigate
        if (isMobile) {
            goToCartPage();
            return;
        }

        onDesktopClick?.() ?? goToCartPage();
    }, [goToCartPage, isMobile, onDesktopClick]);

    return (
        <BaseMiniCartButton
            data-mini-cart-button
            iconColor={getColorVar('secondary', '0')}
            onClick={handleClick}
            {...restProps}
            itemCount={amoutCartItem}
            className={cn(styles.container, restProps?.className)}
        />
    );
});
